import { FeatureFlagKey } from "@redotech/feature-flags-model";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import ChevronLeftIcon from "@redotech/redo-icons/chevron-left.svg";
import ChevronRightIcon from "@redotech/redo-icons/chevron-right.svg";
import Copy01Icon from "@redotech/redo-icons/copy-01.svg";
import Edit04Icon from "@redotech/redo-icons/edit-04.svg";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import { RedoPlatformAuthRpcClientContext } from "@redotech/redo-merchant-app-common/platform/redo-platform-rpc-client-provider";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import { encodeAdvancedFilterGroup } from "@redotech/redo-web/advanced-filters/advanced-filter";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoButtonDropdown } from "@redotech/redo-web/arbiter-components/buttons/redo-dropdown-button";
import {
  RedoCommandMenu,
  type RedoCommandMenuItem,
} from "@redotech/redo-web/arbiter-components/command-menu/redo-command-menu";
import { Flex } from "@redotech/redo-web/flex";
import {
  Action0PortalContext,
  ActionPortalContext,
} from "@redotech/redo-web/page";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { BulkActionProgressBar } from "../actions/bulk-action-progress-bar";
import { BulkConversationActionsBar } from "../actions/bulk-conversation-actions-bar";
import { NewConversationModal } from "../components/new-conversation-modal";
import { ActiveConversationFilters } from "../filters/active-conversation-filters";
import { ConversationsFilterToolbar } from "../filters/conversations-filter-toolbar";
import { useViewManagement } from "../hooks/use-view-management";
import { ConversationsTable } from "../table/components/conversations-table";
import { useConversationsTableContextMaybe } from "../table/conversations-table-context";
import * as styles from "./conversations-table-view.module.css";
import { DefaultView, OPEN_VIEW_FILTER_GROUP } from "./default-views";
import { SupportV2ViewModal } from "./views/support-v2-view-modal";

const PaginationControls = memo(function PaginationControls() {
  const tableContext = useConversationsTableContextMaybe();

  if (!tableContext) {
    return null;
  }

  const {
    currentPage,
    pageSize,
    totalCount,
    hasPreviousPage,
    hasNextPage,
    goToNextPage,
    goToPreviousPage,
  } = tableContext;

  const startIndex = currentPage * pageSize + 1;
  const endIndex = Math.min((currentPage + 1) * pageSize, totalCount);

  // Don't show pagination if no results
  if (totalCount === 0) {
    return null;
  }

  return (
    <Flex align="center" gap="xs">
      <RedoButton
        disabled={!hasPreviousPage}
        hierarchy="tertiary"
        IconLeading={ChevronLeftIcon}
        onClick={goToPreviousPage}
        size="sm"
      />
      <Text fontSize="xs" textColor="secondary">
        {startIndex}-{endIndex} of {totalCount}
      </Text>
      <RedoButton
        disabled={!hasNextPage}
        hierarchy="tertiary"
        IconLeading={ChevronRightIcon}
        onClick={goToNextPage}
        size="sm"
      />
    </Flex>
  );
});

export const ConversationsTableView = memo(function ConversationsTableView() {
  const navigate = useNavigate();
  const team = useRequiredContext(TeamContext);
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const tableContext = useConversationsTableContextMaybe();
  const [searchParams] = useSearchParams();
  const action0Portal = useContext(Action0PortalContext);

  const handleNavigateAfterCreate = useHandler(
    async ({ conversationId }: { conversationId: string }) => {
      navigate(`/stores/${team._id}/support-v2/${conversationId}`);
    },
  );

  // Context may be null during route transition animations
  if (!tableContext) {
    return null;
  }

  const { selectedConversationCount } = tableContext;

  // Redirect to open default view only when landing on the bare route with no params
  if (
    !searchParams.get("view") &&
    !searchParams.get("defaultView") &&
    !searchParams.get("fg")
  ) {
    const params = new URLSearchParams();
    params.set("defaultView", DefaultView.OPEN);
    params.set("fg", encodeAdvancedFilterGroup(OPEN_VIEW_FILTER_GROUP));
    return <Navigate replace to={`?${params.toString()}`} />;
  }

  return (
    <>
      {action0Portal && createPortal(<SwitchToClassicButton />, action0Portal)}
      <Flex
        className={styles.fadeIn}
        dir="column"
        gap="xl"
        height="full"
        overflow="hidden"
        p="lg"
        w="full"
      >
        <Flex dir="column" gap="xl">
          <Flex align="center" justify="space-between">
            <ConversationsFilterToolbar />
            {selectedConversationCount > 0 ? (
              <BulkConversationActionsBar />
            ) : (
              <Flex align="center" gap="sm">
                <PaginationControls />
                <ViewManagementButtons />
                <RedoButton
                  hierarchy="primary"
                  IconLeading={PlusIcon}
                  onClick={() => setCreateModalOpen(true)}
                  text="Create new ticket"
                />
              </Flex>
            )}
          </Flex>
          <ActiveConversationFilters />
        </Flex>
        <BulkActionProgressBar />
        <Flex className="min-h-0" grow={1} overflow="hidden">
          <ConversationsTable />
        </Flex>
        <NewConversationModal
          onCreated={(conversationId) =>
            void handleNavigateAfterCreate({ conversationId })
          }
          open={createModalOpen}
          setOpen={setCreateModalOpen}
        />
      </Flex>
    </>
  );
});

const ViewManagementButtons = memo(function ViewManagementButtons() {
  const actionsPortal = useContext(ActionPortalContext);
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editDropdownOpen, setEditDropdownOpen] = useState(false);
  const [editDropdownRef, setEditDropdownRef] = useState<HTMLElement | null>(
    null,
  );

  const {
    activeView,
    activeDefaultView,
    filtersMatchView,
    hasActiveFilters,
    reservedViewNames,
    reservedViewNamesExcludingCurrent,
    handleCreateView,
    handleUpdateViewFilters,
    handleEditViewName,
    handleDeleteView,
    handleCancel,
  } = useViewManagement();

  const deleteViewMenuItem: RedoCommandMenuItem = useMemo(
    () => ({
      Icon: TrashIcon,
      text: "Delete view",
      onClick: handleDeleteView,
      theme: "destructive" as const,
    }),
    [handleDeleteView],
  );

  const editViewNameMenuItem: RedoCommandMenuItem = useMemo(
    () => ({
      Icon: Edit04Icon,
      text: "Edit view name",
      onClick: () => setEditModalOpen(true),
    }),
    [],
  );

  const saveAsNewViewMenuItem: RedoCommandMenuItem = useMemo(
    () => ({
      Icon: Copy01Icon,
      text: "Save as new view",
      onClick: () => setSaveModalOpen(true),
    }),
    [],
  );

  // On a default view — nothing to show
  if (activeDefaultView) {
    return null;
  }

  // No active view with active filters — show "Save as new view" + "Cancel" in the page header
  if (!activeView && hasActiveFilters) {
    return (
      <>
        {actionsPortal &&
          createPortal(
            <Flex align="center" gap="sm">
              <RedoButton
                hierarchy="secondary"
                onClick={handleCancel}
                size="sm"
                text="Cancel"
              />
              <RedoButton
                hierarchy="primary"
                onClick={() => setSaveModalOpen(true)}
                size="sm"
                text="Save as new view"
              />
            </Flex>,
            actionsPortal,
          )}
        <SupportV2ViewModal
          currentName=""
          isOpen={saveModalOpen}
          mode="create"
          onClose={() => setSaveModalOpen(false)}
          onSubmit={handleCreateView}
          reservedViewNames={reservedViewNames}
        />
      </>
    );
  }

  // No active view, no active filters — nothing to show
  if (!activeView) {
    return null;
  }

  // On a saved view with changed filters — "Update view" + dropdown
  if (!filtersMatchView) {
    return (
      <>
        <RedoButtonDropdown
          dropdownOpen={editDropdownOpen}
          onClick={handleUpdateViewFilters}
          ref={setEditDropdownRef}
          setDropdownOpen={setEditDropdownOpen}
          size="sm"
          text="Update view"
        >
          <RedoCommandMenu
            anchor={editDropdownRef}
            items={[
              saveAsNewViewMenuItem,
              editViewNameMenuItem,
              deleteViewMenuItem,
            ]}
            open={editDropdownOpen}
            setOpen={setEditDropdownOpen}
          />
        </RedoButtonDropdown>
        <SupportV2ViewModal
          currentName=""
          isOpen={saveModalOpen}
          mode="create"
          onClose={() => setSaveModalOpen(false)}
          onSubmit={handleCreateView}
          reservedViewNames={reservedViewNames}
        />
        {editModalOpen && (
          <SupportV2ViewModal
            currentName={activeView.name}
            isOpen={editModalOpen}
            mode="edit"
            onClose={() => setEditModalOpen(false)}
            onSubmit={async (name) => {
              await handleEditViewName(name);
              setEditModalOpen(false);
            }}
            reservedViewNames={reservedViewNamesExcludingCurrent}
          />
        )}
      </>
    );
  }

  // On a saved view with matching filters — "Edit view" + dropdown with delete
  return (
    <>
      <RedoButtonDropdown
        dropdownOpen={editDropdownOpen}
        onClick={() => setEditModalOpen(true)}
        ref={setEditDropdownRef}
        setDropdownOpen={setEditDropdownOpen}
        size="sm"
        text="Edit view"
      >
        <RedoCommandMenu
          anchor={editDropdownRef}
          items={[deleteViewMenuItem]}
          open={editDropdownOpen}
          setOpen={setEditDropdownOpen}
        />
      </RedoButtonDropdown>
      {editModalOpen && (
        <SupportV2ViewModal
          currentName={activeView.name}
          isOpen={editModalOpen}
          mode="edit"
          onClose={() => setEditModalOpen(false)}
          onSubmit={async (name) => {
            await handleEditViewName(name);
            setEditModalOpen(false);
          }}
          reservedViewNames={reservedViewNamesExcludingCurrent}
        />
      )}
    </>
  );
});

const SwitchToClassicButton = memo(function SwitchToClassicButton() {
  const navigate = useNavigate();
  const team = useRequiredContext(TeamContext);
  const platformRpcClient = useRequiredContext(
    RedoPlatformAuthRpcClientContext,
  );
  const [isLoading, setIsLoading] = useState(false);

  const handleSwitchToClassic = useHandler(async () => {
    setIsLoading(true);
    try {
      await platformRpcClient.removeTeamFromFeatureFlag({
        flagKey: FeatureFlagKey.SUPPORT_HELPDESK_V2,
      });

      navigate(`/stores/${team._id}/support/table/all`);
    } catch (error) {
      console.error("Failed to switch to classic experience:", error);
    } finally {
      setIsLoading(false);
    }
  });

  return (
    <RedoButton
      disabled={isLoading}
      hierarchy="tertiary"
      onClick={handleSwitchToClassic}
      size="sm"
      text="Switch back to the classic experience"
    />
  );
});
